import React, { useEffect, useState } from "react"
import { Dimmer, Form, Icon, Input, Label, Loader, Message, Popup, Table, TextArea } from "semantic-ui-react"
import { sanitizeNoteText, validateYubikCode } from "../../../utils/regexUtils"
import "./YubikCodesTable.css"

const MAX_NOTE_LENGTH = 500

/**
 * Tabella di visualizzazione e modifica dei codici Yubik associati al progetto.
 * Mostra solo le righe con is_deleted = 0 (filtrate lato API).
 *
 * Props:
 *   yubikCodes    {Array}     Righe provenienti dallo store Redux (GET /projects/:id/offerCodes)
 *                             Struttura attesa per ogni riga: { id, version, yubik_offer_codes, note }
 *   editable      {boolean}   true quando il form del progetto e in modalita edit
 *   onRowsChange  {Function}  Callback chiamata ad ogni modifica delle righe: onRowsChange(rows)
 *                             Usata dal container per raccogliere i dati aggiornati al salvataggio
 */
const YubikCodesTable = ({ yubikCodes, editable, onRowsChange, loading }) => {
	const [rows, setRows] = useState([])
	const [newCodeInputs, setNewCodeInputs] = useState({})
	const [codeErrors, setCodeErrors] = useState({})
	const [noteErrors, setNoteErrors] = useState({})

	useEffect(() => {
		const data = Array.isArray(yubikCodes) ? yubikCodes : []
		setRows(data.map(r => ({ ...r, yubik_offer_codes: [...(r.yubik_offer_codes || [])] })))
		setNewCodeInputs(data.reduce((acc, r) => ({ ...acc, [r.id]: "" }), {}))
		setCodeErrors({})
		setNoteErrors({})
	}, [yubikCodes, editable])

	useEffect(() => {
		if (editable && onRowsChange) {
			onRowsChange(rows)
		}
	}, [rows, editable, onRowsChange])

	if (loading) return (
		<div className="yubik-loader-wrapper">
			<Dimmer active inverted><Loader content="Loading" /></Dimmer>
		</div>
	)

	if (!rows.length) return null

	const handleRemoveCode = (rowId, codeIdx) => {
		setRows(prev =>
			prev.map(r =>
				r.id === rowId
					? { ...r, yubik_offer_codes: r.yubik_offer_codes.filter((_, i) => i !== codeIdx) }
					: r
			)
		)
	}

	const handleAddCode = (rowId) => {
		const code = (newCodeInputs[rowId] || "").trim()
		if (!code) return

		if (!validateYubikCode(code)) {
			setCodeErrors(prev => ({
				...prev,
				[rowId]: "Invalid format. Expected: T_CCC_YYYY_AAAA or T_CCC_YYYY_AAAA_VVV (e.g., O_ITA_2025_A001)"
			}))
			return
		}

		const row = rows.find(r => r.id === rowId)
		if (row && row.yubik_offer_codes.includes(code)) {
			setCodeErrors(prev => ({ ...prev, [rowId]: "This code has already been added" }))
			return
		}

		setRows(prev =>
			prev.map(r =>
				r.id === rowId
					? { ...r, yubik_offer_codes: [...r.yubik_offer_codes, code] }
					: r
			)
		)
		setNewCodeInputs(prev => ({ ...prev, [rowId]: "" }))
		setCodeErrors(prev => ({ ...prev, [rowId]: "" }))
	}

	const handleKeyDown = (e, rowId) => {
		if (e.key === "Enter") {
			e.preventDefault()
			handleAddCode(rowId)
		}
	}

	const handleNoteChange = (rowId, value) => {
		if (value.length <= MAX_NOTE_LENGTH) {
			setRows(prev => prev.map(r => (r.id === rowId ? { ...r, note: value } : r)))
			setNoteErrors(prev => ({ ...prev, [rowId]: "" }))
		}
	}

	const handleNotePaste = (e, rowId) => {
		const pastedText = e.clipboardData.getData("text")
		const sanitized = sanitizeNoteText(pastedText)
		const row = rows.find(r => r.id === rowId)
		const currentNote = row ? (row.note || "") : ""
		const selStart = e.target.selectionStart
		const selEnd = e.target.selectionEnd
		const newLength = currentNote.length - (selEnd - selStart) + sanitized.length

		if (newLength > MAX_NOTE_LENGTH) {
			e.preventDefault()
			setNoteErrors(prev => ({
				...prev,
				[rowId]: `Cannot paste: the text would exceed the maximum limit of ${MAX_NOTE_LENGTH} characters (${newLength} characters total)`
			}))
		}
	}

	return (
		<div className="yubik-wrapper">
			<h4 className="yubik-title">Yubik Offer Codes</h4>
			<Table celled compact="very" size="small" className="yubik-table">
				<Table.Header>
					<Table.Row>
						<Table.HeaderCell className="yubik-col-version">Version</Table.HeaderCell>
						<Table.HeaderCell className="yubik-col-codes">Codes</Table.HeaderCell>
						<Table.HeaderCell className="yubik-col-note">Note</Table.HeaderCell>
					</Table.Row>
				</Table.Header>
				<Table.Body>
					{rows.map(row => (
						<Table.Row key={row.id}>

							<Table.Cell collapsing>{row.version}</Table.Cell>

							<Table.Cell>
								<div className="yubik-codes-container">
									{row.yubik_offer_codes.map((code, i) => (
										<Label key={i} size="small">
											{code}
											{editable && (
												<Icon
													name="delete"
													className="yubik-delete-icon"
													onClick={() => handleRemoveCode(row.id, i)}
												/>
											)}
										</Label>
									))}
									{editable && (
										<div className="yubik-add-code-row">
											<Popup
												open={!!codeErrors[row.id]}
												content={codeErrors[row.id] || ""}
												position="bottom left"
												trigger={
													<Input
														size="mini"
														placeholder="T_CCC_YYYY_AAAA"
														value={newCodeInputs[row.id] || ""}
														className="yubik-code-input"
														error={!!codeErrors[row.id]}
														onChange={(e, data) => {
															setNewCodeInputs(prev => ({ ...prev, [row.id]: data.value.toUpperCase() }))
															setCodeErrors(prev => ({ ...prev, [row.id]: "" }))
														}}
														onKeyDown={e => handleKeyDown(e, row.id)}
														action={{
															icon: "add",
															size: "mini",
															onClick: () => handleAddCode(row.id),
														}}
													/>
												}
											/>
											<Popup
												content="Expected format: T_CCC_YYYY_AAAA or T_CCC_YYYY_AAAA_VVV (e.g., O_ITA_2025_A001, C_ESP_2024_B123_V01)"
												trigger={<Icon name="info circle" className="yubik-info-icon" />}
												position="top center"
											/>
										</div>
									)}
								</div>
							</Table.Cell>

							<Table.Cell>
								{editable ? (
									<Form>
										<div className="yubik-note-counter-wrapper">
											<span className={`yubik-note-counter${(row.note || "").length === MAX_NOTE_LENGTH ? " yubik-note-counter--limit" : ""}`}>
												{(row.note || "").length}/{MAX_NOTE_LENGTH}
											</span>
										</div>
										<TextArea
											rows={2}
											value={row.note || ""}
											className="yubik-note-textarea"
											onChange={e => handleNoteChange(row.id, e.target.value)}
											onPaste={e => handleNotePaste(e, row.id)}
										/>
										{noteErrors[row.id] && (
											<Message negative size="tiny" className="yubik-note-error">
												{noteErrors[row.id]}
											</Message>
										)}
									</Form>
								) : (
									<span style={{ whiteSpace: "pre-wrap" }}>{row.note}</span>
								)}
							</Table.Cell>

						</Table.Row>
					))}
				</Table.Body>
			</Table>
		</div>
	)
}

export default YubikCodesTable
